import { Trans } from '@lingui/macro';
import intersection from 'lodash/intersection';
import { useState } from 'react';
import { Button } from '../Button';
import { Link } from '../Link';

type QuizSlug = string;
type AnswerSlug = string;

type RecommendationAnswer = {
  text: string;
  slug: AnswerSlug;
};

type RecommendationQuestion = {
  text: string;
  answers: RecommendationAnswer[];
};

type Recommendation = {
  id: number;
  imgUrl: string;
  name: string;
  description: string;
  answersMatches: AnswerSlug[];
};

type RecommendationsMap = {
  [x: string]: Recommendation[];
};

type ComputedRecommendationsMap = {
  [x: string]: Recommendation;
};

const quizImageUrl = '/images/quiz/valentin.png';

const questions: RecommendationQuestion[] = [
  {
    text: 'Préférez-vous une journée de St Valentin classique ou insolite ?',
    answers: [
      { slug: '1-classique', text: 'Classique' },
      { slug: '1-insolite', text: 'Insolite' },
    ],
  },
  {
    text: 'Êtes-vous plutôt aventurier ou accro au confort ?',
    answers: [
      { slug: '2-confort', text: 'Confort' },
      { slug: '2-aventure', text: 'Aventure' },
    ],
  },
  {
    text: 'Préférez-vous une ambiance initimiste ou festive ?',
    answers: [
      { slug: '3-intimiste', text: 'Intimiste' },
      { slug: '3-festive', text: 'Festive' },
    ],
  },
  {
    text: 'Petit ou grand budget ?',
    answers: [
      { slug: '4-petit', text: 'Petit' },
      { slug: '4-grand', text: 'Grand' },
    ],
  },
  {
    text: 'Nouvelle relation amoureuse ou en couple depuis longtemps ?',
    answers: [
      { slug: '5-nouvelle', text: 'Nouvelle relation' },
      { slug: '5-longtemps', text: 'En couple depuis longtemps' },
    ],
  },
  {
    text: 'Préférez-vous les restaurants chics ou détendus ?',
    answers: [
      { slug: '6-chic', text: 'Chic' },
      { slug: '6-detendu', text: 'Détendu' },
    ],
  },
  {
    text: 'Êtes vous plutôt du type "Ressentir" ou "Penser" ?',
    answers: [
      { slug: '7-ressentir', text: 'Ressentir' },
      { slug: '7-penser', text: 'Penser' },
    ],
  },
  {
    text: 'Aimeriez vous une cuisine aux influences spécifiques ou une cuisine pour tous ?',
    answers: [
      { slug: '8-tous', text: 'Pour Tous' },
      { slug: '8-specifique', text: 'Spécifique' },
    ],
  },
];

const recommendations: RecommendationsMap = {
  restaurant: [
    {
      id: 4496,
      name: 'La vache folle',
      description: 'Cuisine du monde',
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/4496/926e1967-80e2-40ad-8663-12976312a81a.jpg',
      answersMatches: ['1-classique', '3-festive', '4-petit', '5-longtemps', '6-detendu', '8-tous'],
    },
    {
      id: 2307,
      name: 'Joséphine',
      description: 'Cuisine du monde',
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/2307/6baed901-7da2-46b8-858e-23a54505d8c1.jpeg',
      answersMatches: ['1-insolite', '3-intimiste', '4-grand', '5-nouvelle', '6-chic', '8-tous'],
    },
    {
      id: 2333,
      name: 'Ibéricos Taverne à Tapas Espagnoles',
      description: 'Cuisine espagnole',
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/2333/66c1e858-e117-4a50-a53d-6e0a49666b5f.jpeg',
      answersMatches: ['1-classique', '3-festive', '4-petit', '5-nouvelle', '6-detendu', '8-specifique'],
    },
    {
      id: 914,
      name: 'Les Soeurs Grises',
      description: 'Cuisine du monde',
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/914/c6651d04-31de-4f7a-9c81-f4c7b440ce6d.jpeg',
      answersMatches: ['1-classique', '3-festive', '4-petit', '5-longtemps', '6-detendu', '8-tous'],
    },
    {
      id: 2302,
      name: 'Tiradito',
      description: 'Cuisine péruvienne',
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/2302/3ae3a435-4059-4108-a3c2-f725b6a72e45.jpeg',
      answersMatches: ['1-insolite', '3-intimiste', '4-grand', '5-nouvelle', '6-chic', '8-specifique'],
    },
    {
      id: 1710,
      name: 'Bleu Raisin',
      description: 'Cuisine française',
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/1710/38efa8163f7a2c9005b2192134c64333.jpeg',
      answersMatches: ['1-classique', '3-intimiste', '4-petit', '5-longtemps', '6-chic', '8-specifique'],
    },
  ],
  activity: [
    {
      id: 2385,
      name: 'Hôtel Zéro1',
      description: 'Hotel',
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/2385/39621275519dc19585438f699e3d5687.jpeg',
      answersMatches: ['1-classique', '2-confort', '3-intimiste', '4-grand', '5-nouvelle', '7-ressentir'],
    },
    {
      id: 4486,
      name: 'Adventure City Games',
      description: `Jeux urbains scénarisés uniques`,
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/4486/a6a2e2b1-de2d-47cb-b2d3-53333d2a3c54.jpg',
      answersMatches: ['1-insolite', '2-aventure', '3-intimiste', '4-petit', '5-longtemps', '7-penser'],
    },
    {
      id: 4486,
      name: 'Adventure City Games',
      description: `Jeux urbains scénarisés uniques`,
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/4486/a6a2e2b1-de2d-47cb-b2d3-53333d2a3c54.jpg',
      answersMatches: ['1-insolite', '2-aventure', '3-intimiste', '4-petit', '5-longtemps', '7-penser'],
    },
    {
      id: 2381,
      name: 'Scandinave Spa',
      description: `Spa au cœur du Vieux-Montréal`,
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/2381/c12439ff-a79a-4163-a022-34bbd253ed4b.jpg',
      answersMatches: ['1-classique', '2-confort', '3-intimiste', '4-petit', '5-nouvelle', '7-ressentir'],
    },
    {
      id: 2435,
      name: `Musée d'art Contemporain de Montréal`,
      description: `Musée contemporain`,
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/2435/77483f2b-85c7-4a8f-8727-ea03f3e8b6e1.jpg',
      answersMatches: ['1-classique', '2-confort', '3-intimiste', '4-petit', '5-nouvelle', '7-penser'],
    },
    {
      id: 2298,
      name: `Musée McCord Stewart`,
      description: `Musée d'expositions stimulantes`,
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/2298/d28da01c-187f-4db4-b635-fe0809d0557c.jpg',
      answersMatches: ['1-classique', '2-confort', '3-intimiste', '4-petit', '5-nouvelle', '7-penser'],
    },
    {
      id: 1713,
      name: `Zéro Gravité`,
      description: `Escalade`,
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/1713/26be17cc-3fc4-49a9-a83b-ed476942938d.jpeg',
      answersMatches: ['1-insolite', '2-aventure', '3-festive', '4-grand', '5-longtemps', '7-ressentir'],
    },
    {
      id: 1686,
      name: `MTL Zipline`,
      description: `Circuit urbain de tyrolienne`,
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/1686/e9e05719-a820-4be8-befb-29549f9f5cc5.jpeg',
      answersMatches: ['1-insolite', '2-aventure', '3-festive', '4-petit', '5-longtemps', '7-ressentir'],
    },
    {
      id: 2303,
      name: `Club Pelicano`,
      description: `Bar branché`,
      imgUrl: 'https://ik.imagekit.io/milo/prd/images-new/2303/5275510f-3e3b-4f36-8864-b67ae8324abe.jpeg',
      answersMatches: ['1-classique', '2-aventure', '3-festive', '5-nouvelle', '7-ressentir'],
    },
  ],
};

const computeRecommendations: (
  recommendations: RecommendationsMap,
  answers: AnswerSlug[]
) => ComputedRecommendationsMap = (recommendations, answers) => {
  const returnedRecommendations = {};

  Object.entries(recommendations).forEach(([name, recs], index) => {
    console.log(`name`, name);
    let highestMatchScore = 0;
    let recIndexWithHighestMatch = index;

    recs.forEach((rec, index) => {
      const matchScore = intersection(answers, rec.answersMatches).length;
      if (matchScore > highestMatchScore) {
        highestMatchScore = matchScore;
        recIndexWithHighestMatch = index;
      }
    });

    returnedRecommendations[name] = recs[recIndexWithHighestMatch];
  });

  return returnedRecommendations;
};

const AnimatedSteps = ({
  length,
  currentStep,
  dotWidth = 20,
  onChangeStep = (step: number) => {},
  stepClickable = false,
}) => {
  const bgStop = (currentStep / (length - 1)) * 100;

  const changeStep = (index) => {
    if (stepClickable) {
      onChangeStep(index);
    }
  };

  return (
    <div className="relative my-2 flex h-2 items-center justify-between overflow-visible">
      {Array(length)
        .fill(0)
        .map((el, index) => (
          <span
            onClick={() => changeStep(index)}
            className={`cursor-pointer rounded-full ${
              (index <= currentStep && 'bg-red') || 'bg-gray-300'
            } transition-all duration-500 `}
            style={{
              width: `${dotWidth}px`,
              height: `${dotWidth}px`,
            }}
          ></span>
        ))}
      <div
        className="absolute left-0 top-0 -z-10 h-full rounded-xl bg-red transition-all duration-300 ease-out"
        style={{
          width: `${bgStop}%`,
        }}
      />
      <div className="absolute left-0 top-0 -z-50 h-full w-full  bg-gray-300" />
    </div>
  );
};

const MiloValentineLogo = (props) => (
  <svg viewBox="0 0 136 70" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M108.276 26.6663C108.28 27.1988 108.126 27.7203 107.834 28.1647C107.542 28.6091 107.125 28.9562 106.636 29.162C106.147 29.3677 105.608 29.4227 105.088 29.3201C104.568 29.2174 104.09 28.9617 103.715 28.5854C103.34 28.2092 103.085 27.7294 102.982 27.2071C102.879 26.6847 102.933 26.1434 103.137 25.652C103.341 25.1606 103.686 24.7412 104.128 24.4472C104.57 24.1532 105.089 23.9979 105.619 24.0009C105.97 23.9919 106.319 24.0546 106.645 24.1853C106.972 24.316 107.268 24.5119 107.516 24.7612C107.765 25.0105 107.96 25.3079 108.091 25.6354C108.222 25.9629 108.285 26.3136 108.276 26.6663ZM126.671 49.0213C124.983 48.9907 123.342 48.4601 121.952 47.496C120.563 46.5319 119.488 45.1772 118.863 43.6018C118.237 42.0265 118.088 40.3005 118.435 38.6406C118.782 36.9806 119.609 35.4604 120.812 34.2707C122.016 33.081 123.542 32.2747 125.2 31.953C126.858 31.6313 128.573 31.8085 130.131 32.4623C131.689 33.1162 133.02 34.2176 133.957 35.6284C134.895 37.0392 135.396 38.6966 135.4 40.3928C135.394 41.536 135.163 42.6667 134.72 43.7199C134.278 44.7731 133.632 45.7281 132.821 46.53C132.01 47.3318 131.049 47.9647 129.994 48.3923C128.938 48.8198 127.809 49.0336 126.671 49.0213ZM126.671 35.8845C125.782 35.8945 124.916 36.1686 124.182 36.6724C123.447 37.1762 122.878 37.8871 122.545 38.7154C122.213 39.5437 122.132 40.4525 122.312 41.3271C122.493 42.2016 122.927 43.0029 123.561 43.6299C124.194 44.2569 124.998 44.6816 125.871 44.8503C126.744 45.0191 127.647 44.9244 128.467 44.5782C129.287 44.232 129.986 43.6498 130.477 42.9049C130.968 42.1601 131.228 41.286 131.225 40.3928C131.212 39.1887 130.726 38.0386 129.873 37.1923C129.021 36.3459 127.87 35.8718 126.671 35.8729V35.8845ZM100.563 48.1794V39.0737H100.575C100.575 33.9097 97.7788 31.7953 94.1561 31.7953C93.1182 31.7774 92.089 31.9888 91.1413 32.4145C90.1936 32.8402 89.3506 33.4698 88.6719 34.2589C87.5326 32.5712 85.6749 31.7953 83.4967 31.7953C81.3184 31.7953 80.2834 32.548 79.2483 33.3239V32.5014C79.2483 32.3985 79.2076 32.2998 79.1352 32.2271C79.0628 32.1543 78.9646 32.1134 78.8621 32.1134H75.3862C75.2838 32.1134 75.1855 32.1543 75.1131 32.2271C75.0407 32.2998 75 32.3985 75 32.5014V48.1639C75 48.2668 75.0407 48.3654 75.1131 48.4382C75.1855 48.511 75.2838 48.5518 75.3862 48.5518H78.8621C78.9646 48.5518 79.0628 48.511 79.1352 48.4382C79.2076 48.3654 79.2483 48.2668 79.2483 48.1639V39.6828C79.2483 36.5286 80.9708 35.8729 82.6624 35.8729C84.1803 35.8729 85.6633 36.3889 85.6633 39.7526V48.1639C85.6628 48.2604 85.6982 48.3536 85.7625 48.4254C85.8267 48.4971 85.9153 48.5422 86.0109 48.5518H89.5177C89.6201 48.5518 89.7184 48.511 89.7908 48.4382C89.8632 48.3654 89.9039 48.2668 89.9039 48.1639V39.6828C89.9039 36.5286 91.6264 35.8729 93.3181 35.8729C94.8359 35.8729 96.3189 36.3889 96.3189 39.7526V48.1794C96.3185 48.2765 96.3544 48.3702 96.4195 48.4421C96.4846 48.5139 96.5741 48.5586 96.6704 48.5674H100.212C100.308 48.5586 100.398 48.5139 100.463 48.4421C100.528 48.3702 100.564 48.2765 100.563 48.1794ZM107.724 32.5014V48.1794C107.724 48.2759 107.689 48.3691 107.625 48.4409C107.56 48.5126 107.472 48.5577 107.376 48.5674H103.831C103.734 48.5586 103.645 48.5139 103.58 48.4421C103.515 48.3702 103.479 48.2765 103.479 48.1794V32.5014C103.479 32.3985 103.52 32.2998 103.592 32.2271C103.665 32.1543 103.763 32.1134 103.866 32.1134H107.341C107.444 32.1134 107.542 32.1543 107.615 32.2271C107.687 32.2998 107.728 32.3985 107.728 32.5014H107.724ZM117.68 44.9674V44.967H117.684C117.683 44.9671 117.682 44.9673 117.68 44.9674ZM117.68 48.742V44.9674C117.464 44.9936 117.246 45.0051 117.028 45.0019C115.545 45.0019 115.236 44.3074 115.236 43.1978V24.7148C115.236 24.6119 115.195 24.5132 115.122 24.4405C115.05 24.3677 114.952 24.3268 114.849 24.3268H111.373C111.271 24.3268 111.173 24.3677 111.1 24.4405C111.028 24.5132 110.987 24.6119 110.987 24.7148V44.1678C110.987 47.2871 112.714 49.0213 115.85 49.0213C116.47 49.0129 117.086 48.9189 117.68 48.742Z"
      fill="#E62152"
    />
    <path
      d="M18.7327 49.4879L13.3827 54.0103L10.1106 53.3179C9.81469 53.2718 9.53741 53.4096 9.30749 53.6063L0.722236 60.9506C0.185409 61.4099 0.258956 62.1451 0.773724 62.3031L4.93292 63.1343C5.2288 63.1803 5.41893 63.4171 5.4079 63.7257L5.42759 68.0984C5.40608 68.7152 6.16883 68.8608 6.70566 68.4015L15.2141 61.1222C15.444 60.9255 15.5788 60.6104 15.6376 60.3611L15.5854 56.9139L21.1375 52.1169L18.7327 49.4879Z"
      fill="#E62152"
    />
    <path
      d="M55.0968 22.3907L60.6288 17.7425C60.3744 17.0843 60.2937 16.2748 60.2675 15.5283C59.4568 15.4647 58.7673 15.1865 58.1106 14.7687L52.8346 19.5878L55.0968 22.3907Z"
      fill="#E62152"
    />
    <path
      d="M68.0285 7.92737L59.0728 7.40006C56.63 7.25622 54.5329 9.08409 54.3056 11.5552C54.1973 12.732 54.6182 13.8802 55.3566 14.7517C56.0949 15.6232 57.0986 16.1554 58.2612 16.2239L59.3077 16.2856L59.1577 17.2821C59.0495 18.459 59.4703 19.6071 60.2087 20.4786C60.9471 21.3501 62.0034 21.9445 63.2301 21.9577C65.6199 22.039 67.6645 20.1491 67.8917 17.678L68.7251 8.61855C68.7571 8.26605 68.4933 7.95475 68.0285 7.92737Z"
      fill="#E62152"
    />
    <path
      d="M60.759 37.8985C60.759 42.9632 59.3213 47.9141 56.6278 52.1253C53.9343 56.3364 50.1059 59.6185 45.6268 61.5567C41.1477 63.4949 36.219 64.002 31.464 63.0139C26.709 62.0259 22.3412 59.587 18.913 56.0057C15.4849 52.4245 13.1502 47.8616 12.2044 42.8943C11.2586 37.9269 11.744 32.7781 13.5993 28.0989C15.4547 23.4198 18.5965 19.4204 22.6276 16.6067C26.6587 13.7929 31.398 12.291 36.2462 12.291C42.7474 12.291 48.9823 14.9889 53.5793 19.7913C58.1764 24.5936 60.759 31.107 60.759 37.8985Z"
      fill="#E62152"
    />
    <path
      d="M24.9141 28.8138C25.5704 29.2604 26.1339 29.8329 26.5727 30.499C26.9572 31.0804 27.2831 31.6994 27.5455 32.3467C28.0372 33.5562 28.4023 34.8146 28.635 36.1012C28.8685 37.3552 29.0099 38.625 29.0582 39.9001C29.0582 40.5407 29.0582 41.1812 29.0582 41.8415C29.0249 42.5409 28.9255 43.2356 28.7615 43.9158C28.5867 44.594 28.3489 45.2539 28.0514 45.8867C27.7297 46.5735 27.3066 47.2065 26.7965 47.764C26.1926 48.4249 25.4145 48.8969 24.5542 49.124C24.0565 49.2433 23.5398 49.2567 23.0367 49.1634C22.5254 49.062 22.0423 48.8484 21.6213 48.5376C20.8716 47.9607 20.3353 47.145 20.0989 46.2218C19.8987 45.4421 19.8524 44.63 19.9627 43.8321C20.0059 43.4656 20.0774 43.1032 20.1767 42.7481C20.2205 42.5707 20.274 42.3983 20.3275 42.2307L20.415 41.9794L20.4978 41.7479C20.7227 41.1384 20.9792 40.5414 21.2662 39.9593C21.8374 38.814 22.5029 37.7196 23.2556 36.6876C23.9852 35.6676 24.7634 34.7167 25.561 33.7805C25.9599 33.3173 26.3587 32.864 26.7673 32.4107L27.0883 32.0609L27.4336 31.7061C27.6671 31.4745 27.92 31.2479 28.1535 31.0311C29.1566 30.135 30.2841 29.393 31.4999 28.8286C32.1473 28.5334 32.8262 28.3151 33.5232 28.1782C34.2733 28.0292 35.0429 28.0092 35.7995 28.1191C36.5993 28.2344 37.3664 28.5181 38.0515 28.9518C38.6989 29.3802 39.2669 29.9205 39.7296 30.5482C40.5346 31.6868 41.1204 32.9689 41.4562 34.3274C41.6265 34.968 41.7578 35.6036 41.8648 36.2343L41.9378 36.727L42.001 37.1803L42.1226 38.0869C42.3148 39.3747 42.3816 40.6784 42.322 41.9794C42.2928 42.3243 42.2588 42.6742 42.2053 43.0043L42.1177 43.497C42.0885 43.6596 42.0594 43.8075 42.0205 43.9898C41.8644 44.7019 41.6226 45.392 41.3006 46.0444C40.9578 46.7401 40.5005 47.3716 39.9484 47.9118C39.3338 48.5149 38.5795 48.952 37.7548 49.1831C37.2913 49.3129 36.8079 49.3531 36.3297 49.3013C35.8128 49.2514 35.313 49.0869 34.8659 48.8196C34.4187 48.5522 34.0351 48.1885 33.7421 47.7542C33.4773 47.3678 33.2768 46.94 33.1487 46.4879C32.9416 45.7165 32.879 44.9126 32.9639 44.1179C33.0381 43.4157 33.1798 42.7226 33.3871 42.0484C33.5833 41.4132 33.8188 40.7911 34.0923 40.1859C34.2236 39.8903 34.3647 39.5947 34.5106 39.3089L34.9435 38.4811C36.1125 36.2413 37.5608 34.1631 39.2529 32.2974C40.1417 31.3272 41.1409 30.4672 42.2296 29.7352C43.407 28.9236 44.7506 28.393 46.1596 28.1831C46.9542 28.0775 47.7616 28.1362 48.5332 28.3556C49.3511 28.5893 50.0985 29.0263 50.7074 29.6268C51.2858 30.2015 51.7382 30.8932 52.0352 31.6569C52.3073 32.3561 52.4792 33.0911 52.5459 33.8396C52.659 35.2125 52.5439 36.5949 52.2054 37.9292L52.0839 38.422L51.9671 38.8556L51.7385 39.708C51.5926 40.2746 51.4564 40.8363 51.3251 41.403C51.1938 41.9696 51.0868 42.5264 50.9895 43.0782C50.8944 43.5994 50.831 44.1259 50.7998 44.6549C50.7672 45.0051 50.8104 45.3582 50.9262 45.6897C50.9635 45.7813 51.0199 45.8637 51.0916 45.9311C51.2158 46.0391 51.3512 46.1333 51.4953 46.2119V46.2119C51.6745 46.3158 51.8317 46.4544 51.958 46.6198C52.0843 46.7852 52.1772 46.9742 52.2314 47.1759C52.2856 47.3777 52.3 47.5883 52.2739 47.7958C52.2477 48.0032 52.1815 48.2034 52.079 48.3849C51.9765 48.5664 51.8396 48.7256 51.6764 48.8536C51.5131 48.9815 51.3266 49.0756 51.1274 49.1305C50.9282 49.1854 50.7203 49.2001 50.5155 49.1736C50.3108 49.1471 50.1132 49.08 49.934 48.9761C49.5597 48.7645 49.2119 48.5081 48.898 48.2124C48.5427 47.8625 48.2556 47.4479 48.0517 46.9905C47.8697 46.5739 47.7501 46.1321 47.6966 45.6798C47.6466 45.2911 47.632 44.8986 47.6528 44.5071C47.6919 43.8458 47.7715 43.1875 47.8912 42.5362C47.9982 41.9105 48.1246 41.2995 48.2608 40.6984C48.397 40.0972 48.5429 39.4961 48.6986 38.9048L48.9369 38.0229L49.0536 37.5893L49.1509 37.2C49.4087 36.2125 49.5008 35.1882 49.4233 34.1698C49.3898 33.3363 49.0718 32.5404 48.5235 31.918C48.2832 31.6814 47.9866 31.5117 47.6626 31.4253C47.2917 31.3251 46.9049 31.2999 46.5244 31.3514C45.5863 31.5044 44.6939 31.868 43.9125 32.4157C43.0253 33.0113 42.2092 33.709 41.4805 34.495C39.9826 36.1526 38.6995 37.9972 37.6624 39.9839L37.2636 40.7427C37.1436 40.9891 37.0269 41.2354 36.9134 41.4818C36.6917 41.9613 36.5016 42.4551 36.3443 42.96C36.1985 43.4287 36.0976 43.9105 36.0427 44.3987C35.9937 44.7957 36.0185 45.1984 36.1157 45.5862C36.1541 45.7936 36.2688 45.9785 36.4367 46.1035C36.5174 46.1452 36.6058 46.1693 36.6963 46.1744C36.7867 46.1795 36.8772 46.1654 36.962 46.1331C37.2785 46.0421 37.5676 45.8727 37.8034 45.6404C38.1031 45.3477 38.35 45.0042 38.533 44.6254C38.7474 44.2068 38.9107 43.7634 39.0194 43.3049C39.0194 43.1915 39.0729 43.0487 39.0973 42.9156C39.1216 42.7826 39.1459 42.6495 39.1654 42.5214C39.2091 42.2603 39.2335 42.0287 39.2529 41.7577C39.2995 40.6656 39.2408 39.5715 39.0778 38.4909L38.9611 37.6238L38.9027 37.1852L38.8395 36.7763C38.747 36.2293 38.6352 35.6923 38.499 35.1749C38.2661 34.2021 37.8571 33.2816 37.2927 32.46C37.0687 32.1286 36.7809 31.8465 36.4466 31.6306C36.1123 31.4146 35.7383 31.2694 35.3472 31.2036C34.4546 31.1204 33.5568 31.291 32.7547 31.6963C31.8178 32.1318 30.9492 32.7046 30.1769 33.3962C29.9775 33.5736 29.7829 33.7559 29.5981 33.9431C29.5008 34.0318 29.4084 34.1303 29.3208 34.224L29.0387 34.5344C28.6448 34.968 28.2605 35.4016 27.8811 35.8401C26.3615 37.5094 25.0543 39.365 23.99 41.3635C23.7467 41.8518 23.5308 42.3535 23.3431 42.8663L23.2701 43.0536L23.2118 43.226C23.1777 43.3443 23.1388 43.4576 23.1097 43.5709C23.0505 43.7909 23.0066 44.0148 22.9783 44.241C22.9179 44.6332 22.9361 45.0337 23.0318 45.4187C23.083 45.6684 23.2147 45.8938 23.4063 46.0592C23.4673 46.1059 23.5383 46.1373 23.6135 46.151C23.6887 46.1647 23.766 46.1603 23.8392 46.138C24.127 46.0485 24.3834 45.8775 24.5785 45.6453C25.1785 44.9258 25.5989 44.0707 25.8042 43.1521C25.9168 42.6668 25.9851 42.172 26.0085 41.674C26.0377 41.1369 26.0426 40.585 26.0085 40.0282C25.9706 38.91 25.8471 37.7963 25.6389 36.6974C25.4503 35.626 25.152 34.5774 24.7488 33.5686C24.5605 33.111 24.3275 32.6736 24.0533 32.2629C23.839 31.9273 23.5632 31.6362 23.241 31.4056V31.4056C23.0644 31.3005 22.9108 31.1602 22.7894 30.9932C22.668 30.8262 22.5813 30.636 22.5346 30.4341C22.4878 30.2322 22.4821 30.0227 22.5176 29.8185C22.5531 29.6142 22.6292 29.4194 22.7412 29.2458C22.8533 29.0722 22.999 28.9235 23.1694 28.8086C23.3399 28.6937 23.5316 28.6151 23.7329 28.5774C23.9342 28.5398 24.141 28.544 24.3407 28.5897C24.5404 28.6354 24.7289 28.7217 24.8947 28.8434L24.9141 28.8138Z"
      fill="white"
    />
  </svg>
);

export const RecommendationQuiz = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentQuestionIndex];
  // const currentQuestion = questions[currentQuestionIndex];
  const [answers, setAnswers] = useState<AnswerSlug[]>([]);
  const [quizFinished, setQuizFinished] = useState(false);
  const [computedRecommendations, setComputedRecommendations] = useState<ComputedRecommendationsMap>({});

  const finishQuiz = () => {
    setQuizFinished(true);
    const computedRecommendations = computeRecommendations(recommendations, answers);
    setComputedRecommendations(computedRecommendations);
  };

  const handleAnswer = (answer: RecommendationAnswer) => {
    if (currentQuestionIndex + 1 >= totalQuestions) {
      return finishQuiz();
    }

    setAnswers((answers) => [...answers, answer.slug]);
    next();
  };

  const length = questions.length;

  const startTest = () => setQuizStarted(true);
  const next = () => setCurrentQuestionIndex((step) => (step + 1 >= length ? length - 1 : step + 1));
  const prev = () => setCurrentQuestionIndex((step) => (step - 1 <= 0 ? 0 : step - 1));

  return (
    <div className="mb-10">
      <div className="mb-12 flex max-h-96 items-start overflow-hidden">
        <img className="w-full" src={quizImageUrl} alt="M ta région quiz saint-valentin" />
      </div>

      <div className="app-container">
        <MiloValentineLogo className="mx-auto mb-10 w-40" />

        {!quizFinished && (
          <>
            <h1 className="mb-12 text-center text-3xl font-medium">
              <Trans>Quiz Spécial Saint-Valentin</Trans>
            </h1>

            <div className="mb-20">
              <AnimatedSteps
                length={length}
                currentStep={currentQuestionIndex}
                onChangeStep={setCurrentQuestionIndex}
              />
            </div>
          </>
        )}

        {!quizStarted && (
          <div className="flex flex-col items-center justify-center rounded-xl bg-red p-20 text-white">
            <h3 className="mb-4 text-2xl font-light">
              <Trans>En moins de 2 minutes,</Trans>
            </h3>
            <h2 className="mb-12 text-center text-3xl font-semibold">
              <Trans>Découvrez le combo activité et restaurant parfait pour votre sortie entre amoureux!</Trans>
            </h2>

            <Button onClick={startTest} variant="secondary" className="min-w-[240px] !border-0 !text-xl !text-red">
              <Trans>Je fais le test</Trans>
            </Button>
          </div>
        )}

        {quizStarted && !quizFinished && (
          <div className="flex animate-fade-in-top flex-col items-center justify-center rounded-xl bg-red p-20 text-white">
            <h3 className="mb-4 text-2xl font-light">
              <Trans>
                Question {currentQuestionIndex + 1} sur {totalQuestions}
              </Trans>
            </h3>
            <h2 className="mb-12 text-center text-3xl font-semibold">{currentQuestion.text}</h2>
            <div className="flex gap-2">
              {currentQuestion.answers.map((answer) => (
                <button
                  onClick={() => handleAnswer(answer)}
                  className="mb-4 w-full rounded-xl bg-white p-4 text-xl font-semibold text-charcoal"
                >
                  {answer.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {quizFinished && (
          <div className="animate-fade-in-top text-center">
            <h2 className="mb-10 text-3xl font-bold">
              <Trans>Voici vos recommandations M ta région!</Trans>
            </h2>
            <div className="mx-auto mb-10 flex w-full max-w-3xl flex-col items-center justify-center gap-5 text-center md:flex-row md:justify-between ">
              <div className="w-3/4 rounded-2xl border-2 p-7 shadow-lg">
                <div className=" mb-4 rounded-2xl p-3">
                  <h3 className="mb-4 text-2xl font-bold text-green">
                    <Trans>Restaurant</Trans>
                  </h3>
                  <img
                    className="mx-auto mb-4 block h-52 rounded-xl"
                    src={computedRecommendations.restaurant.imgUrl}
                    alt={computedRecommendations.restaurant.name}
                  />

                  <p className="mb-2 text-2xl font-semibold">{computedRecommendations.restaurant.name}</p>
                  <p>{computedRecommendations.restaurant.description}</p>
                </div>
                <Link target="_blank" href={`/experiences/${computedRecommendations.restaurant.id}`}>
                  <Button variant="secondary">Voir l'offre</Button>
                </Link>
              </div>

              <div className="w-3/4 rounded-2xl border-2 p-7 shadow-lg">
                <div className="mb-4 rounded-2xl p-3">
                  <h3 className="mb-4 text-2xl font-bold text-warning">
                    <Trans>Activité</Trans>
                  </h3>
                  <img
                    className="mx-auto mb-4 block h-52 rounded-xl"
                    src={computedRecommendations.activity.imgUrl}
                    alt={computedRecommendations.activity.name}
                  />
                  <p className="mb-2 text-2xl font-semibold">{computedRecommendations.activity.name}</p>
                  <p>{computedRecommendations.activity.description}</p>
                </div>
                <Link target="_blank" href={`/experiences/${computedRecommendations.activity.id}`}>
                  <Button variant="secondary">Voir l'offre</Button>
                </Link>
              </div>
            </div>

            <h2 className="mb-16 text-2xl font-bold">
              <Trans>
                Pour profiter du rabais en visitant ces expériences locales, utilisez votre pass M ta région!
              </Trans>
            </h2>

            <div className="rounded-2xl border bg-red px-5 py-10 text-white shadow-md">
              <h2 className="text-3xl font-bold">
                <Trans>Vous n'avez pas encore de pass M ta région ?</Trans>
              </h2>

              <h2 className="mb-5 text-3xl font-bold">
                <Trans>En ce moment:</Trans>
              </h2>

              <h2 className="mb-12 text-2xl font-light">
                <Trans>20% de réduction sur le pass M ta région 1 mois avec le code "VALENTIN2023"</Trans>
              </h2>

              <Link href="/purchase/checkout?promoCode=VALENTIN2023&productId=milo_pass_one_month">
                <Button variant="secondary" className="!border-0 text-xl">
                  <Trans>J'en profite!</Trans>
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
